import { getTextureInternalFormat, TextureFormat } from "@/core/backends/webgl/texture-format"
import { getTextureFilter, getTextureWrap, TextureFilter, TextureWrap } from "./texture"
import { uuid } from "@/utils/uuid"
import { getBytesPerPixel } from "./texture-size"
import { WebGLBackend } from "../webgl-backend"

export interface TextureArrayOptions {
	label: string
	width: number
	height: number
	layers: number
	format: TextureFormat
	mips?: boolean
	min?: TextureFilter
	mag?: TextureFilter
	wrapS?: TextureWrap
	wrapT?: TextureWrap
}

export class TextureArray {
	static framebuffer: WebGLFramebuffer | null = null

	private backend: WebGLBackend

	private get gl() {
		return this.backend.gl
	}

	id: string = ""
	label: string

	textureArray: WebGLTexture
	format: TextureFormat
	width: number
	height: number
	layers: number

	constructor(backend: WebGLBackend, options: TextureArrayOptions) {
		this.id = uuid()
		this.backend = backend
		this.textureArray = createTextureArray(this.gl, options)
		this.format = options.format
		this.width = options.width
		this.height = options.height
		this.layers = options.layers
		this.label = options.label

		this.backend.textureMemoryCounters.add(this)
	}

	bind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_2D_ARRAY, this.textureArray)

		this.backend.textureMemoryCounters.update(this)
	}

	unbind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_2D, null)
	}

	bindFramebuffer(layerIndex: number) {
		TextureArray.framebuffer = TextureArray.framebuffer || this.gl.createFramebuffer()

		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, TextureArray.framebuffer)
		this.gl.framebufferTextureLayer(
			this.gl.FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.textureArray,
			0,
			layerIndex,
		)

		if (import.meta.env.DEV) {
			if (
				this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) != this.gl.FRAMEBUFFER_COMPLETE
			) {
				throw new Error(
					"Rendering to this texture array is not supported (incomplete framebuffer)",
				)
			}
		}

		this.gl.viewport(0, 0, this.width, this.height)
	}

	bindDrawFramebuffer(layerIndex: number) {
		TextureArray.framebuffer = TextureArray.framebuffer || this.gl.createFramebuffer()

		this.gl.bindFramebuffer(this.gl.DRAW_FRAMEBUFFER, TextureArray.framebuffer)
		this.gl.framebufferTextureLayer(
			this.gl.DRAW_FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.textureArray,
			0,
			layerIndex,
		)

		if (import.meta.env.DEV) {
			if (
				this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) != this.gl.FRAMEBUFFER_COMPLETE
			) {
				throw new Error(
					"Rendering to this texture array is not supported (incomplete framebuffer)",
				)
			}
		}

		this.gl.viewport(0, 0, this.width, this.height)
	}

	getBytesPerPixel() {
		const internalFormat = getTextureInternalFormat(this.gl, this.format)
		return getBytesPerPixel(this.gl, internalFormat)
	}

	getByteLength() {
		const size = this.width * this.height * this.layers
		return size * this.getBytesPerPixel()
	}

	delete() {
		this.gl.deleteTexture(this.textureArray)

		this.backend.textureMemoryCounters.delete(this)
	}
}

function createTextureArray(gl: WebGL2RenderingContext, options: TextureArrayOptions) {
	const textureArray = gl.createTexture()

	if (textureArray == null) {
		throw new Error("Could not create texture array")
	}

	const minFilter = getTextureFilter(options.min ?? "linear", options.mips)
	const magFilter = getTextureFilter(options.mag ?? "linear", false)
	const wrapS = getTextureWrap(options.wrapS ?? "clamp-to-edge")
	const wrapT = getTextureWrap(options.wrapT ?? "clamp-to-edge")

	gl.bindTexture(gl.TEXTURE_2D_ARRAY, textureArray)
	gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, minFilter)
	gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, magFilter)
	gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, wrapS)
	gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, wrapT)

	const internalFormat = getTextureInternalFormat(gl, options.format)
	gl.texStorage3D(
		gl.TEXTURE_2D_ARRAY,
		1,
		internalFormat,
		options.width,
		options.height,
		options.layers,
	)

	//setTextureData(gl, options)

	return textureArray
}
